import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { toast } from "sonner";
import { Briefcase, Upload, IdentificationCard, IdentificationBadge, CheckCircle, ArrowRight, Lock } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useT } from "../lib/i18n";
import { api } from "../lib/api";

/**
 * AgentApply — public page for agent applications. Captures applicant identity
 * (name, address, phone, email, DL number) + uploads front + back of their
 * driver license to object storage. Admin reviews + approves in /admin/agents.
 */
export default function AgentApply() {
  const nav = useNavigate();
  const t = useT();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", address: "", dl_number: "", ssn: "",
    password: "", password_confirm: "", why_join: "",
    website_url: "",  // honeypot — must stay empty; backend silently rejects if filled
    sms_consent: false,
  });
  const [files, setFiles] = useState({ front: null, back: null, ssn_card: null });
  const [previews, setPreviews] = useState({ front: null, back: null, ssn_card: null });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onFile = (side, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpe?g|png|webp)$/.test(f.type)) {
      toast.error(t("apply.toast.invalid_image"));
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      toast.error(t("apply.toast.too_large"));
      return;
    }
    setFiles((p) => ({ ...p, [side]: f }));
    setPreviews((p) => ({ ...p, [side]: URL.createObjectURL(f) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!files.front || !files.back) {
      toast.error(t("apply.toast.upload_both"));
      return;
    }
    const ssnDigits = (form.ssn || "").replace(/\D/g, "");
    if (ssnDigits.length !== 9) {
      toast.error(t("apply.toast.invalid_ssn"));
      return;
    }
    if ((form.password || "").length < 8) {
      toast.error(t("apply.toast.password_too_short"));
      return;
    }
    if (form.password !== form.password_confirm) {
      toast.error(t("apply.toast.password_mismatch"));
      return;
    }
    if (!form.sms_consent) {
      toast.error(t("apply.toast.sms_consent_required"));
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "password_confirm") return;       // not sent to server
        if (k === "sms_consent") { fd.append(k, v ? "true" : "false"); return; }
        fd.append(k, k === "ssn" ? ssnDigits : v);
      });
      fd.append("dl_front", files.front);
      fd.append("dl_back", files.back);
      if (files.ssn_card) fd.append("ssn_card", files.ssn_card);
      // Use axios via shared api client. Raw `fetch()` was failing with
      // "body stream already read" because PostHog session recording
      // monkey-patches window.fetch and consumes the response body before
      // our .json() call. Axios uses XHR and is not affected.
      await api.post("/agent-applications", fd);
      setSubmitted(true);
      toast.success(t("apply.toast.submitted"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (err.message || t("apply.toast.failed")));
    } finally {
      setBusy(false);
    }
  };

  // Auto-format SSN as the user types: 123456789 → 123-45-6789
  const onSsnChange = (v) => {
    const digits = (v || "").replace(/\D/g, "").slice(0, 9);
    let formatted = digits;
    if (digits.length > 5) formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    else if (digits.length > 3) formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    setForm({ ...form, ssn: formatted });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FBF9F6]">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-20" data-testid="agent-apply-success">
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-10 text-center">
            <CheckCircle size={48} weight="duotone" className="mx-auto text-emerald-600" />
            <h1 className="serif text-4xl mt-4">{t("apply.success.title")}</h1>
            <p className="text-[#4A5C50] mt-3">
              {t("apply.success.desc", { name: form.full_name.split(" ")[0], email: form.email })}
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Button onClick={() => nav("/")} variant="outline">{t("apply.success.back_home")}</Button>
              <Button onClick={() => nav("/support")} className="bg-[#184A30] text-white">{t("apply.success.contact_support")}</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-14">
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] flex items-center gap-2">
          <Briefcase size={14} weight="duotone" /> {t("apply.eyebrow")}
        </p>
        <h1 className="serif text-5xl mt-2" data-testid="agent-apply-heading">{t("apply.heading")}</h1>
        <p className="text-[#4A5C50] mt-3 max-w-xl">{t("apply.intro")}</p>

        <ul className="mt-6 space-y-2 text-sm text-[#112318]">
          <li className="flex items-start gap-2"><CheckCircle size={16} className="mt-0.5 text-emerald-700" /> {t("apply.bullet1")}</li>
          <li className="flex items-start gap-2"><CheckCircle size={16} className="mt-0.5 text-emerald-700" /> {t("apply.bullet2")}</li>
          <li className="flex items-start gap-2"><CheckCircle size={16} className="mt-0.5 text-emerald-700" /> {t("apply.bullet3")}</li>
        </ul>

        <form onSubmit={submit} className="bg-white border border-[#E5E0D8] rounded-2xl mt-10 p-7 space-y-5" data-testid="agent-apply-form">
          {/*
            Honeypot. Visually hidden but present in the DOM (display:none would
            tip off smarter bots). Real users can't see it; only bots that
            blindly fill every form input will populate it. Backend silently
            drops any submission with a non-empty value here.
          */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
            <label>Website (do not fill)
              <input
                type="text"
                name="website_url"
                tabIndex={-1}
                autoComplete="off"
                value={form.website_url}
                onChange={e => setForm({ ...form, website_url: e.target.value })}
                data-testid="apply-honeypot"
              />
            </label>
          </div>
          <h2 className="serif text-2xl">{t("apply.section.your_info")}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("apply.field.full_name")} req val={form.full_name} on={v => setForm({ ...form, full_name: v })} testId="apply-fullname" placeholder={t("apply.field.full_name_ph")} />
            <Field label={t("apply.field.email")} type="email" req val={form.email} on={v => setForm({ ...form, email: v })} testId="apply-email" mono />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("apply.field.phone")} req val={form.phone} on={v => setForm({ ...form, phone: v })} testId="apply-phone" mono placeholder={t("apply.field.phone_ph")} />
            <Field label={t("apply.field.dl_number")} req val={form.dl_number} on={v => setForm({ ...form, dl_number: v })} testId="apply-dl-number" mono />
          </div>
          <div>
            <Label>{t("apply.field.ssn")}<span className="text-rose-600"> *</span></Label>
            <input
              type="text"
              value={form.ssn}
              onChange={(e) => onSsnChange(e.target.value)}
              placeholder={t("apply.field.ssn_ph")}
              maxLength={11}
              autoComplete="off"
              inputMode="numeric"
              required
              className="w-full mt-1 rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30] mono tracking-widest"
              data-testid="apply-ssn"
            />
            <p className="text-[11px] text-[#4A5C50] mt-1 flex items-start gap-1">
              <Lock size={11} weight="duotone" className="mt-0.5 shrink-0 text-[#184A30]" />
              {t("apply.field.ssn_helper")}
            </p>
          </div>
          <Field label={t("apply.field.address")} req val={form.address} on={v => setForm({ ...form, address: v })} testId="apply-address" placeholder={t("apply.field.address_ph")} />

          <div className="border-t border-[#E5E0D8] pt-4 mt-4">
            <p className="text-xs tracking-[0.25em] uppercase text-[#4A5C50] mb-1">
              {t("apply.section.choose_password")}
            </p>
            <p className="text-[11px] text-[#4A5C50] mb-3">
              {t("apply.section.choose_password_helper")}
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label={t("apply.field.password")} type="password" req val={form.password} on={v => setForm({ ...form, password: v })} testId="apply-password" placeholder={t("apply.field.password_ph")} />
              <Field label={t("apply.field.password_confirm")} type="password" req val={form.password_confirm} on={v => setForm({ ...form, password_confirm: v })} testId="apply-password-confirm" placeholder={t("apply.field.password_confirm_ph")} />
            </div>
          </div>
          <div>
            <Label>{t("apply.field.why_join_label")}</Label>
            <textarea
              value={form.why_join} onChange={e => setForm({ ...form, why_join: e.target.value.slice(0, 1000) })}
              rows={3} className="w-full mt-1 rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30]"
              placeholder={t("apply.field.why_join_ph")} data-testid="apply-why"
            />
          </div>

          <div className="border-t border-[#E5E0D8] pt-5">
            <h2 className="serif text-2xl">{t("apply.dl.section")}</h2>
            <p className="text-xs text-[#4A5C50] mt-1">{t("apply.dl.helper")}</p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <DLUpload side="front" label={t("apply.dl.front")} tapLabel={t("apply.dl.tap_upload")} metaLabel={t("apply.dl.file_meta")} file={files.front} preview={previews.front} onPick={(e) => onFile("front", e)} testId="apply-dl-front" />
              <DLUpload side="back" label={t("apply.dl.back")} tapLabel={t("apply.dl.tap_upload")} metaLabel={t("apply.dl.file_meta")} file={files.back} preview={previews.back} onPick={(e) => onFile("back", e)} testId="apply-dl-back" />
            </div>
          </div>

          <div className="border-t border-[#E5E0D8] pt-5">
            <h2 className="serif text-2xl">{t("apply.ssn_card.section")}</h2>
            <p className="text-xs text-[#4A5C50] mt-1">{t("apply.ssn_card.helper")}</p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <DLUpload
                side="ssn_card"
                label={t("apply.ssn_card.label")}
                tapLabel={t("apply.dl.tap_upload")}
                metaLabel={t("apply.dl.file_meta")}
                file={files.ssn_card}
                preview={previews.ssn_card}
                onPick={(e) => onFile("ssn_card", e)}
                testId="apply-ssn-card"
                icon={<IdentificationBadge size={14} weight="duotone" />}
                optional
              />
            </div>
          </div>

          <div className="border-t border-[#E5E0D8] pt-5">
            <label
              className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-[#E5E0D8] bg-stone-50 hover:bg-stone-100 transition-colors"
              data-testid="apply-sms-consent-label"
            >
              <input
                type="checkbox"
                checked={form.sms_consent}
                onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })}
                className="mt-1 h-5 w-5 rounded border-[#E5E0D8] text-[#184A30] focus:ring-[#184A30] shrink-0"
                data-testid="apply-sms-consent-checkbox"
              />
              <span className="text-xs text-[#112318] leading-relaxed">
                {t("apply.sms_consent.label")} <Link to="/sms-policy" target="_blank" rel="noopener" className="text-[#184A30] underline" data-testid="apply-sms-consent-policy-link">{t("apply.sms_consent.policy_link")}</Link>, <Link to="/privacy" target="_blank" rel="noopener" className="text-[#184A30] underline">{t("apply.sms_consent.privacy_link")}</Link>, {t("apply.sms_consent.and")} <Link to="/terms" target="_blank" rel="noopener" className="text-[#184A30] underline">{t("apply.sms_consent.terms_link")}</Link>.
                <span className="block mt-1 text-[10px] text-[#4A5C50]">{t("apply.sms_consent.fineprint")}</span>
              </span>
            </label>
          </div>

          <div className="flex items-center justify-between pt-3">
            <Link to="/" className="text-sm text-[#4A5C50] underline">{t("apply.cancel")}</Link>
            <Button type="submit" disabled={busy} className="bg-[#184A30] text-white text-base px-6 py-5 rounded-xl" data-testid="apply-submit">
              {busy ? t("apply.submit_busy") : <>{t("apply.submit")} <ArrowRight size={16} className="ml-2" /></>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, val, on, type = "text", req, mono, testId, placeholder }) {
  return (
    <div>
      <Label>{label}{req && <span className="text-rose-600"> *</span>}</Label>
      <input
        type={type} value={val} onChange={e => on(e.target.value)} required={!!req}
        placeholder={placeholder || ""}
        className={`w-full mt-1 rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30] ${mono ? "mono" : ""}`}
        data-testid={testId}
      />
    </div>
  );
}

function DLUpload({ side, label, tapLabel, metaLabel, file, preview, onPick, testId, icon, optional = false }) {
  return (
    <label className="block cursor-pointer" data-testid={testId}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#4A5C50] mb-2 flex items-center gap-2">
        {icon || <IdentificationCard size={14} weight="duotone" />}
        {label}
        {optional
          ? <span className="text-[#4A5C50] opacity-70 normal-case tracking-normal text-[10px]">(optional)</span>
          : <span className="text-rose-600">*</span>}
      </div>
      <div className="bg-stone-50 border-2 border-dashed border-[#E5E0D8] rounded-xl aspect-[16/10] flex flex-col items-center justify-center text-center p-4 hover:border-[#184A30] transition-colors overflow-hidden">
        {preview ? (
          <img src={preview} alt={`Upload preview ${side}`} className="max-w-full max-h-full object-contain rounded" />
        ) : (
          <>
            <Upload size={28} weight="duotone" className="text-[#184A30]" />
            <p className="mt-2 text-sm font-semibold text-[#112318]">{tapLabel}</p>
            <p className="text-xs text-[#4A5C50]">{metaLabel}</p>
          </>
        )}
      </div>
      {file && (
        <p className="text-xs text-emerald-700 mt-2 mono truncate">✓ {file.name} ({Math.round(file.size / 1024)} KB)</p>
      )}
      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} className="sr-only" />
    </label>
  );
}
